import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { Star, Plus, X, TrendingUp, Search, Lock, Loader2 } from 'lucide-react';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { SignalBadge } from '../components/common/Badge';
import { LockedFeature } from '../components/common/LockedFeature';
import { UpgradePrompt } from '../components/common/UpgradePrompt';
import { StarterBadge } from '../components/common/TierBadge';
import { StockLogo } from '../components/common/StockLogo';
import { useWatchlist } from '../hooks/useWatchlist';
import { getWatchlistStockData, type WatchlistStockData } from '../utils/api';

export function Watchlist() {
  const navigate = useNavigate();
  const {
    watchlist,
    loading,
    hasWatchlistAccess,
    maxTickers,
    atLimit,
    nearLimit,
    userLevel,
    addTicker,
    removeTicker,
  } = useWatchlist();

  const [newTicker, setNewTicker] = useState('');
  const [showLimitWarning, setShowLimitWarning] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [removingTicker, setRemovingTicker] = useState<string | null>(null);
  const [stockData, setStockData] = useState<Record<string, WatchlistStockData>>({});
  const [loadingStockData, setLoadingStockData] = useState(false);

  const isPro = userLevel >= 2;

  // Fetch stock data for all watchlist tickers
  useEffect(() => {
    if (watchlist.length === 0) {
      setStockData({});
      return;
    }

    const fetchStockData = async () => {
      setLoadingStockData(true);
      try {
        const result = await getWatchlistStockData(watchlist);
        setStockData(result.stocks);
      } catch (error) {
        console.error('Failed to fetch watchlist stock data:', error);
      } finally {
        setLoadingStockData(false);
      }
    };

    fetchStockData();
  }, [watchlist]);

  const handleAddTicker = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTicker.trim() || isAdding) return;

    // Check limit for non-pro users
    if (atLimit) {
      setShowLimitWarning(true);
      return;
    }

    const ticker = newTicker.trim().toUpperCase();
    if (!watchlist.includes(ticker)) {
      setIsAdding(true);
      await addTicker(ticker);
      setIsAdding(false);
    }
    setNewTicker('');
  };

  const handleRemove = async (ticker: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigation to analysis page
    setRemovingTicker(ticker);
    await removeTicker(ticker);
    setRemovingTicker(null);
    setShowLimitWarning(false);
  };

  // Get data for watchlist stocks from fetched data
  const getStockDataForTicker = (ticker: string) => {
    return stockData[ticker];
  };

  const content = (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-[var(--color-text-primary)]">
              My Watchlist
            </h1>
            <p className="text-sm text-[var(--color-text-muted)] mt-1">
              Track your favorite stocks and monitor their sentiment signals
              {maxTickers !== null && (
                <span className="ml-2 text-xs">
                  ({watchlist.length}/{maxTickers} tickers)
                </span>
              )}
            </p>
          </div>
          {!isPro && <StarterBadge size="md" />}
        </div>

        <form onSubmit={handleAddTicker} className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--color-text-muted)]" />
            <input
              type="text"
              value={newTicker}
              onChange={(e) => setNewTicker(e.target.value.toUpperCase())}
              placeholder="Add ticker..."
              disabled={atLimit}
              className={clsx(
                'w-40 h-10 pl-10 pr-4 rounded-lg',
                'bg-[var(--color-bg-primary)] border border-[var(--color-border)]',
                'text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)]',
                'focus:outline-none focus:ring-2 focus:ring-blue-500/50',
                atLimit && 'opacity-50 cursor-not-allowed'
              )}
            />
          </div>
          <Button
            type="submit"
            icon={
              isAdding ? <Loader2 className="h-4 w-4 animate-spin" /> :
              atLimit ? <Lock className="h-4 w-4" /> :
              <Plus className="h-4 w-4" />
            }
            disabled={atLimit || isAdding}
          >
            {isAdding ? 'Adding...' : atLimit ? 'Limit' : 'Add'}
          </Button>
        </form>
      </div>

      {/* Limit Warning for Starter users */}
      {showLimitWarning && !isPro && (
        <UpgradePrompt
          currentTier="starter"
          limitType="10 ticker"
          proValue="unlimited"
        />
      )}

      {/* Near limit warning */}
      {nearLimit && !atLimit && !isPro && (
        <UpgradePrompt
          currentTier="starter"
          limitType="10 ticker"
          proValue="unlimited"
          compact
        />
      )}

      {/* Watchlist */}
      {watchlist.length === 0 && !loading ? (
        <Card className="text-center py-16 flex flex-col items-center justify-center">
          <Star className="h-16 w-16 text-[var(--color-text-muted)] mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-[var(--color-text-primary)]">
            Your Watchlist is Empty
          </h3>
          <p className="text-[var(--color-text-muted)] mt-2 max-w-md mx-auto">
            Add stocks to your watchlist to monitor their sentiment and get notified of significant
            changes.
          </p>
        </Card>
      ) : (
        <div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {watchlist.map((ticker) => {
            const data = getStockDataForTicker(ticker);
            const isLoadingThis = loadingStockData && !data;

            return (
              <Card key={ticker} hover className="relative group overflow-hidden">
                {/* Remove Button */}
                <button
                  onClick={(e) => handleRemove(ticker, e)}
                  disabled={removingTicker === ticker}
                  className={clsx(
                    'absolute top-3 right-3 p-1.5 rounded-lg cursor-pointer',
                    'opacity-0 group-hover:opacity-100 transition-opacity',
                    'bg-red-500/10 text-red-500 hover:bg-red-500/20',
                    removingTicker === ticker && 'opacity-100'
                  )}
                >
                  {removingTicker === ticker ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <X className="h-4 w-4" />
                  )}
                </button>

                <div
                  onClick={() => navigate(`/analysis?ticker=${ticker}`)}
                  className="cursor-pointer overflow-hidden"
                >
                  <div className="flex items-center gap-3 mb-4 min-w-0">
                    <StockLogo ticker={ticker} name={data?.name} size="xl" />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-[var(--color-text-primary)] truncate">
                        {ticker}
                      </h3>
                      {data && (
                        <p className="text-sm text-[var(--color-text-muted)] truncate">
                          {data.name}
                        </p>
                      )}
                    </div>
                    {data && (
                      <div className="flex-shrink-0">
                        <SignalBadge signal={data.signal} />
                      </div>
                    )}
                  </div>

                  {isLoadingThis ? (
                    <div className="flex items-center justify-center gap-2 p-4 rounded-lg bg-[var(--color-bg-secondary)]">
                      <Loader2 className="h-5 w-5 text-[var(--color-text-muted)] animate-spin" />
                      <span className="text-sm text-[var(--color-text-muted)]">
                        Loading data...
                      </span>
                    </div>
                  ) : data ? (
                    <div className="grid grid-cols-4 gap-1.5">
                      <div className="text-center p-1.5 rounded-lg bg-[var(--color-bg-secondary)] overflow-hidden">
                        <p
                          className={clsx(
                            'text-base font-bold font-mono truncate',
                            data.disconnect_score >= 60
                              ? 'text-emerald-500'
                              : data.disconnect_score <= 40
                              ? 'text-red-500'
                              : 'text-amber-500'
                          )}
                        >
                          {data.disconnect_score.toFixed(0)}
                        </p>
                        <p className="text-[10px] text-[var(--color-text-muted)] truncate">Disconnect</p>
                      </div>
                      <div className="text-center p-1.5 rounded-lg bg-[var(--color-bg-secondary)] overflow-hidden">
                        <p
                          className={clsx(
                            'text-base font-bold font-mono truncate',
                            data.sentiment_score >= 60
                              ? 'text-emerald-500'
                              : data.sentiment_score <= 40
                              ? 'text-red-500'
                              : 'text-amber-500'
                          )}
                        >
                          {data.sentiment_score.toFixed(0)}
                        </p>
                        <p className="text-[10px] text-[var(--color-text-muted)] truncate">Sentiment</p>
                      </div>
                      <div className="text-center p-1.5 rounded-lg bg-[var(--color-bg-secondary)] overflow-hidden">
                        <p
                          className={clsx(
                            'text-base font-bold font-mono truncate',
                            data.fundamental_score >= 60
                              ? 'text-emerald-500'
                              : data.fundamental_score <= 40
                              ? 'text-red-500'
                              : 'text-amber-500'
                          )}
                        >
                          {data.fundamental_score.toFixed(0)}
                        </p>
                        <p className="text-[10px] text-[var(--color-text-muted)] truncate">Fundament.</p>
                      </div>
                      <div className="text-center p-1.5 rounded-lg bg-[var(--color-bg-secondary)] overflow-hidden">
                        <p className="text-base font-bold font-mono text-[var(--color-text-primary)] truncate">
                          ${data.current_price?.toFixed(2) || '—'}
                        </p>
                        <p className="text-[10px] text-[var(--color-text-muted)] truncate">Price</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2 p-4 rounded-lg bg-[var(--color-bg-secondary)]">
                      <TrendingUp className="h-5 w-5 text-[var(--color-text-muted)]" />
                      <span className="text-sm text-[var(--color-text-muted)]">
                        Click to view analysis
                      </span>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
          </div>
        </div>
      )}
    </div>
  );

  // For free users, wrap content in LockedFeature
  if (!hasWatchlistAccess) {
    return (
      <LockedFeature feature="Custom watchlists to track your favorite stocks" requiredTier="starter">
        {content}
      </LockedFeature>
    );
  }

  return content;
}
